'use client';

import { ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { LucideIcon, ArrowLeft, Building2, ChevronDown } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useTranslation } from '@/hooks/useTranslation';

interface PageHeaderProps {
  /** 页面标题 */
  title: string;
  /** 标题图标（可选） */
  icon?: LucideIcon;
  /** 搜索/筛选区域（与标题同行） */
  filters?: ReactNode;
  /** 右侧操作区域 */
  actions?: ReactNode;
  /** 返回链接（设置后显示返回按钮） */
  backUrl?: string;
  /** 返回行为（优先级高于 backUrl，适用于 router.back 等场景） */
  onBack?: () => void;
}

/**
 * 绩效模块页面顶部组件
 * 遵循统一规范：固定高度 h-12，标题+搜索筛选+操作按钮在同一行
 * 内置组织选择器：当用户属于多个组织时自动显示
 */
export function PageHeader({
  title,
  icon: Icon,
  filters,
  actions,
  backUrl,
  onBack,
}: PageHeaderProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const { organizations, currentOrganization, setCurrentOrganization } = useOrganization();

  const showBackButton = Boolean(onBack || backUrl);
  const showOrgSelector = organizations.length > 1;

  return (
    <div className="bg-white border-b" style={{ borderColor: '#e5e6eb' }}>
      <div className="px-6 h-12 flex items-center justify-between">
        {/* 左侧：标题 + 搜索筛选 */}
        <div className="flex items-center gap-4 flex-1">
          {/* 标题 */}
          <h1 className="text-base font-semibold flex items-center shrink-0" style={{ color: '#1f2329' }}>
            {showBackButton && (
              <button
                onClick={() => {
                  if (onBack) return onBack();
                  if (backUrl) return router.push(backUrl);
                }}
                className="p-1 -ml-1 mr-2 hover:bg-gray-100 rounded transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
            )}
            {Icon && <Icon className="w-5 h-5 mr-2 text-blue-600" />}
            {title}
          </h1>

          {/* 搜索/筛选区域 */}
          {filters && (
            <div className="flex items-center gap-3 flex-1">
              {filters}
            </div>
          )}
        </div>

        {/* 右侧：操作按钮 + 组织选择器 */}
        <div className="flex items-center gap-3 shrink-0">
          {actions}
          {showOrgSelector && (
            <div className="relative">
              <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <select
                value={currentOrganization?.id ?? ''}
                onChange={(e) => {
                  const org = e.target.value
                    ? organizations.find((o) => o.id === e.target.value) ?? null
                    : null;
                  setCurrentOrganization(org);
                }}
                className="appearance-none pl-7 pr-7 h-8 text-sm rounded-md border bg-white cursor-pointer"
                style={{ borderColor: '#e5e6eb', color: '#1f2329' }}
              >
                <option key="__all__" value="">{t.performance?.organization?.all || '全部组织'}</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.displayName || org.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
